import "leaflet/dist/leaflet.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./AboutModal.css";
import { leader_index } from "./alto_types/alto_types.js";
import "./App.css";
import AppLayout from "./components/AppLayout";
import HomePage from "./components/HomePage";
import { NavigationPage } from "./components/Sidebar";
import { Cluster, getClusterConfig, getClusters } from "./config";
import "./ErrorNotification.css";
import "./SearchModal.css";
import "./styles/design-system.css";
import {
  FinalizedJs,
  NotarizedJs,
  SeedJs,
  ViewData,
} from "./types";
import { useClockSkew } from "./useClockSkew";
import { useConsensusWebSocket } from "./useConsensusWebSocket";
import { hexToUint8Array } from "./utils";

import {
  HEALTH_CHECK_INTERVAL,
  MAX_VIEWS_STORED,
  MOBILE_BREAKPOINT,
  TIMEOUT_DURATION,
  VIEW_UPDATE_INTERVAL,
} from "./constants";
import MaintenancePage from "./MaintenancePage";

const getInitialCluster = (): Cluster => {
  // Otherwise, use the USA cluster
  return "usa";
};

const App: React.FC = () => {
  const [selectedCluster, setSelectedCluster] = useState<Cluster>(
    getInitialCluster()
  );
  const clusterConfig = useMemo(
    () => getClusterConfig(selectedCluster),
    [selectedCluster]
  );
  const allConfigs = useMemo(() => getClusters(), []);
  const { BACKEND_URL, PUBLIC_KEY_HEX, LOCATIONS } = clusterConfig;
  const PUBLIC_KEY = useMemo(
    () => hexToUint8Array(PUBLIC_KEY_HEX),
    [PUBLIC_KEY_HEX]
  );

  const [views, setViews] = useState<ViewData[]>([]);
  const [lastObservedView, setLastObservedView] = useState<number | null>(null);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState<boolean>(false);
  const [isKeyInfoModalOpen, setIsKeyInfoModalOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<NavigationPage>("home");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [isInMaintenance, setIsInMaintenance] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const adjustTime = useClockSkew();
  const currentTimeRef = useRef(adjustTime(Date.now()));

  // WebSocket connection reset function (will be set by hook)
  const wsResetRef = useRef<() => void>(() => {});

  const performClusterSwitch = useCallback((cluster: Cluster) => {
    console.log(`Switching to ${cluster} cluster`);
    wsResetRef.current();
    setSelectedCluster(cluster);
  }, []);

  const handleClusterChange = (cluster: Cluster) => {
    if (cluster !== selectedCluster) {
      // Update URL and push to history
      const url = new URL(window.location.href);
      url.searchParams.set("cluster", cluster);
      window.history.pushState({ cluster }, "", url.toString());

      // Perform the cluster switch
      performClusterSwitch(cluster);
    }
  };

  // Effect to handle browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const newCluster = getInitialCluster();
      if (newCluster !== selectedCluster) {
        performClusterSwitch(newCluster);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [selectedCluster, performClusterSwitch]);

  // Reset state when the cluster changes
  useEffect(() => {
    setViews([]);
    setLastObservedView(null);
    setErrorMessage("");
    setShowError(false);
  }, [selectedCluster]);

  // Health check function
  const checkHealth = useCallback(async () => {
    try {
      const protocol = BACKEND_URL.includes(":") ? "http" : "https";
      const response = await fetch(`${protocol}://${BACKEND_URL}/health`, {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      if (response.status !== 200) {
        console.error(`Health check failed with status: ${response.status}`);
        setIsInMaintenance(true);
        return false;
      } else {
        setIsInMaintenance(false);
        return true;
      }
    } catch (error) {
      console.error("Health check failed:", error);
      setIsInMaintenance(true);
      return false;
    } finally {
      // Mark loading as complete regardless of result
      setIsLoading(false);
    }
  }, [BACKEND_URL]);

  // Run health check on initial load and periodically
  useEffect(() => {
    // Run initial health check immediately - this will set isLoading to false when complete
    checkHealth();

    // Only set up periodic checks after the initial check
    const setupInterval = () => {
      healthCheckIntervalRef.current = setInterval(
        checkHealth,
        HEALTH_CHECK_INTERVAL
      );
    };

    // Wait for the initial check before setting up the interval
    const initialCheckTimeout = setTimeout(setupInterval, 1000);

    return () => {
      // Clean up interval and timeout on unmount
      clearTimeout(initialCheckTimeout);
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [checkHealth]);

  // Check for mobile viewport
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // Initial check
    checkIfMobile();

    // Add resize listener
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const handleSeed = useCallback(
    (seed: SeedJs) => {
      const view = seed.view + 1; // Next view is determined by seed - 1

      setViews((prevViews) => {
        // Create a copy of the current views that we'll modify
        let newViews = [...prevViews];

        // If we haven't observed any views yet, or if the new view is greater than the last observed view + 1,
        // handle potentially missed views
        if (lastObservedView === null || view > lastObservedView + 1) {
          const startViewIndex =
            lastObservedView !== null ? lastObservedView + 1 : view;

          // Add any missed views as skipped/timed out
          for (
            let missedView = startViewIndex;
            missedView < view;
            missedView++
          ) {
            // Check if this view already exists
            const existingIndex = newViews.findIndex(
              (v) => v.view === missedView
            );

            if (existingIndex === -1) {
              // Set a timeout for unknown views
              const timeoutId = setTimeout(() => {
                setViews((currentViews) => {
                  return currentViews.map((v) => {
                    // Only time out this specific view if it's still in unknown state
                    if (v.view === missedView && v.status === "unknown") {
                      return {
                        ...v,
                        status: "timed_out",
                        timeoutId: undefined,
                      };
                    }
                    return v;
                  });
                });
              }, TIMEOUT_DURATION);

              // Only add if it doesn't already exist
              newViews.unshift({
                view: missedView,
                location: undefined,
                locationName: undefined,
                status: "unknown",
                startTime: adjustTime(Date.now()),
                timeoutId: timeoutId,
              });
            }
          }
        }

        // Check if this view already exists
        const existingIndex = newViews.findIndex((v) => v.view === view);

        if (existingIndex !== -1) {
          // If it exists and is already finalized or notarized, just update
          // the location and signature information without changing timing
          const existingStatus = newViews[existingIndex].status;
          if (
            existingStatus === "finalized" ||
            existingStatus === "notarized"
          ) {
            const locationIndex = leader_index(seed, LOCATIONS.length);

            // Only update location and signature info, preserve all timing and status
            newViews[existingIndex] = {
              ...newViews[existingIndex],
              location: LOCATIONS[locationIndex][0],
              locationName: LOCATIONS[locationIndex][1],
              signature: seed.signature,
            };

            return newViews;
          }

          // Skip processing for views with "unknown" status
          if (existingStatus === "unknown") {
            return newViews;
          }

          // If it exists but is in another state, clear its timeout but preserve everything else
          if (newViews[existingIndex].timeoutId) {
            clearTimeout(newViews[existingIndex].timeoutId);
          }
        }

        // Create the new view data
        const locationIndex = leader_index(seed, LOCATIONS.length);
        const newView: ViewData = {
          view,
          location: LOCATIONS[locationIndex][0],
          locationName: LOCATIONS[locationIndex][1],
          status: "growing",
          startTime: adjustTime(Date.now()),
          signature: seed.signature,
        };

        // Set a timeout for this specific view
        const timeoutId = setTimeout(() => {
          setViews((currentViews) => {
            return currentViews.map((v) => {
              // Only time out this specific view if it's still in growing state
              if (v.view === view && v.status === "growing") {
                return { ...v, status: "timed_out", timeoutId: undefined };
              }
              return v;
            });
          });
        }, TIMEOUT_DURATION);

        // Add timeoutId to the new view
        const viewWithTimeout = { ...newView, timeoutId };

        // Update or add the view
        if (existingIndex !== -1) {
          // Only update if necessary - preserve existing data that shouldn't change
          newViews[existingIndex] = {
            ...newViews[existingIndex],
            status: "growing",
            signature: seed.signature,
            timeoutId: timeoutId,
            location: LOCATIONS[locationIndex][0],
            locationName: LOCATIONS[locationIndex][1],
          };
        } else {
          // Add as new
          newViews.unshift(viewWithTimeout);
        }

        // Update the last observed view if this is a new maximum
        if (lastObservedView === null || view > lastObservedView) {
          setLastObservedView(view);
        }

        // Limit the number of views
        if (newViews.length > MAX_VIEWS_STORED) {
          // Clean up any timeouts for views we're about to remove
          for (let i = MAX_VIEWS_STORED; i < newViews.length; i++) {
            if (newViews[i].timeoutId) {
              clearTimeout(newViews[i].timeoutId);
            }
          }
          newViews = newViews.slice(0, MAX_VIEWS_STORED);
        }

        return newViews;
      });
    },
    [lastObservedView, adjustTime, LOCATIONS]
  );

  const handleNotarization = useCallback(
    (notarized: NotarizedJs) => {
      const view = notarized.proof.view;
      setViews((prevViews) => {
        const index = prevViews.findIndex((v) => v.view === view);

        // If the view exists and is already finalized, ignore this notarization completely
        if (index !== -1 && prevViews[index].status === "finalized") {
          return prevViews; // No changes needed, preserve finalized state
        }
        let newViews = [...prevViews];
        const currentTime = adjustTime(Date.now());

        // Calculate a reasonable start time using the block timestamp if available
        let calculatedStartTime = currentTime;
        if (notarized.block && notarized.block.timestamp) {
          // The block timestamp is in milliseconds since epoch
          const blockTime = Number(notarized.block.timestamp);
          calculatedStartTime = blockTime;
        }

        if (index !== -1) {
          const viewData = prevViews[index];
          // Clear timeout if it exists
          if (viewData.timeoutId) {
            clearTimeout(viewData.timeoutId);
          }

          // Calculate actual notarization latency when we receive the notarization message
          let actualNotarizationLatency: number | undefined = undefined;
          if (notarized.block && notarized.block.timestamp) {
            const blockTime = Number(notarized.block.timestamp);
            if (blockTime > 0 && blockTime < currentTime) {
              actualNotarizationLatency = currentTime - blockTime;
            }
          }

          // Update the view with notarization data
          const updatedView: ViewData = {
            ...viewData,
            status: "notarized", // We already checked it's not finalized
            notarizationTime: currentTime,
            // If no start time exists, use the block timestamp
            startTime: viewData.startTime || calculatedStartTime,
            block: viewData.block || notarized.block, // Don't overwrite existing block data
            timeoutId: undefined,
            actualNotarizationLatency,
          };

          newViews = [
            ...prevViews.slice(0, index),
            updatedView,
            ...prevViews.slice(index + 1),
          ];
        } else {
          // If view doesn't exist, create it with block timestamp as start time
          let actualNotarizationLatency: number | undefined = undefined;
          if (notarized.block && notarized.block.timestamp) {
            const blockTime = Number(notarized.block.timestamp);
            if (blockTime > 0 && blockTime < currentTime) {
              actualNotarizationLatency = currentTime - blockTime;
            }
          }
          newViews = [
            {
              view,
              location: undefined,
              locationName: undefined,
              status: "notarized",
              startTime: calculatedStartTime,
              notarizationTime: currentTime,
              block: notarized.block,
              actualNotarizationLatency,
            },
            ...prevViews,
          ];
        }

        // Limit the number of views
        if (newViews.length > MAX_VIEWS_STORED) {
          // Clean up any timeouts for views we're about to remove
          for (let i = MAX_VIEWS_STORED; i < newViews.length; i++) {
            if (newViews[i].timeoutId) {
              clearTimeout(newViews[i].timeoutId);
            }
          }
          newViews = newViews.slice(0, MAX_VIEWS_STORED);
        }

        return newViews;
      });
    },
    [adjustTime]
  );

  const handleFinalization = useCallback(
    (finalized: FinalizedJs) => {
      const view = finalized.proof.view;
      setViews((prevViews) => {
        const index = prevViews.findIndex((v) => v.view === view);
        let newViews = [...prevViews];
        const currentTime = adjustTime(Date.now());

        // Calculate a reasonable start time using the block timestamp if available
        let calculatedStartTime = currentTime;
        if (finalized.block && finalized.block.timestamp) {
          // The block timestamp is in milliseconds since epoch
          const blockTime = Number(finalized.block.timestamp);
          calculatedStartTime = blockTime;
        }

        if (index !== -1) {
          const viewData = prevViews[index];
          // Clear timeout if it exists
          if (viewData.timeoutId) {
            clearTimeout(viewData.timeoutId);
          }

          // Calculate actual finalization latency when we receive the finalization message
          let actualFinalizationLatency: number | undefined = undefined;
          if (finalized.block && finalized.block.timestamp) {
            const blockTime = Number(finalized.block.timestamp);
            if (blockTime > 0 && blockTime < currentTime) {
              actualFinalizationLatency = currentTime - blockTime;
            }
          }

          // If already finalized, don't update
          if (viewData.status === "finalized") {
            return prevViews;
          }

          // Use existing data if available, without fabricating missing data
          const updatedView: ViewData = {
            ...viewData,
            status: "finalized",
            finalizationTime: currentTime,
            // Keep existing notarization time if available, but don't create one if missing
            // Keep existing start time or use block timestamp if none
            startTime: viewData.startTime || calculatedStartTime,
            block: finalized.block,
            timeoutId: undefined,
            actualNotarizationLatency: viewData.actualNotarizationLatency,
            actualFinalizationLatency,
          };

          newViews = [
            ...prevViews.slice(0, index),
            updatedView,
            ...prevViews.slice(index + 1),
          ];
        } else {
          // If view doesn't exist, create it with just the data we have
          let actualFinalizationLatency: number | undefined = undefined;
          if (finalized.block && finalized.block.timestamp) {
            const blockTime = Number(finalized.block.timestamp);
            if (blockTime > 0 && blockTime < currentTime) {
              actualFinalizationLatency = currentTime - blockTime;
            }
          }
          newViews = [
            {
              view,
              location: undefined,
              locationName: undefined,
              status: "finalized",
              startTime: calculatedStartTime,
              // No notarization time observed yet
              finalizationTime: currentTime,
              block: finalized.block,
              actualFinalizationLatency,
            },
            ...prevViews,
          ];
        }

        // Limit the number of views
        if (newViews.length > MAX_VIEWS_STORED) {
          // Clean up any timeouts for views we're about to remove
          for (let i = MAX_VIEWS_STORED; i < newViews.length; i++) {
            if (newViews[i].timeoutId) {
              clearTimeout(newViews[i].timeoutId);
            }
          }
          newViews = newViews.slice(0, MAX_VIEWS_STORED);
        }

        return newViews;
      });
    },
    [adjustTime]
  );

  // Update current time every 50ms to force re-render for growing bars
  useEffect(() => {
    const interval = setInterval(() => {
      currentTimeRef.current = adjustTime(Date.now());
      // Force re-render without relying on state updates
      setViews((views) => [...views]);
    }, VIEW_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [adjustTime]);

  // WebSocket connection using custom hook
  const { error: wsError, clearError: clearWsError, resetConnection } = useConsensusWebSocket({
    backendUrl: BACKEND_URL,
    publicKey: PUBLIC_KEY,
    enabled: !isLoading && !isInMaintenance,
    onSeed: handleSeed,
    onNotarized: handleNotarization,
    onFinalized: handleFinalization,
  });

  // Update wsResetRef with the resetConnection function
  useEffect(() => {
    wsResetRef.current = resetConnection;
  }, [resetConnection]);

  // Sync WebSocket errors with component state
  useEffect(() => {
    if (wsError) {
      setErrorMessage(wsError);
      setShowError(true);
    } else {
      clearWsError();
    }
  }, [wsError, clearWsError]);

  // Loading state - show nothing until we get the result of the health check
  if (isLoading) {
    return null;
  }

  // If we're in maintenance mode, show the maintenance page
  if (isInMaintenance) {
    return <MaintenancePage />;
  }

  const renderPageContent = () => {
 
        return (
          <HomePage
            views={views}
            selectedCluster={selectedCluster}
            configs={allConfigs}
            locationsCount={LOCATIONS.length}
            onClusterChange={handleClusterChange}
          />
        );
  
  };

  return (
    <AppLayout currentPage={currentPage} onNavigate={setCurrentPage}>
      {renderPageContent()}
    </AppLayout>
  );
};

export default App;
